import Checkbox from '@/components/Checkbox/Checkbox';
import inputStyles from '@/components/Input/Input.module.css';
import SortIcon from '@/components/Tables/SortIcon';
import useFranchiseeAlias from '@/utils/useFranchiseeAlias/useFranchiseeAlias';
import {
  ColumnDef,
  ColumnFiltersState,
  Row,
  SortingState,
  Table as TableType,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  Children,
} from 'react';
import {
  arrow,
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import styles from './FZMenuTable.module.css';
import { TableCardProps } from './FZMenuTable.types';
import { StoreProduct } from '@/types/menus.types';
import { useCurrentUser } from '@/services/user/user.service';
import { UserRoles } from '@/types/auth.types';

// Helper function to check if any store group has an active price
const hasActivePrice = (storeGroups: StoreProduct['storeGroups']): boolean => {
  return storeGroups.some((sg) => {
    const priceRange = sg.chosenPriceRange;
    // Check if price exists and is not just "Inactive" (could be "Inactive-10.69" or "10.69")
    return priceRange && priceRange !== 'Inactive' && priceRange.trim() !== '';
  });
};

// Custom IconWithTooltip component for styled tooltips
const IconWithTooltip = ({
  children,
  tooltipText,
  placement = 'top',
}: {
  children: React.ReactNode;
  tooltipText: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
}) => {
  const [open, setOpen] = useState(false);
  const arrowRef = useRef(null);

  const {
    x,
    y,
    reference,
    floating,
    strategy,
    context,
    middlewareData: { arrow: { x: arrowX, y: arrowY } = {} },
  } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(12), flip(), shift(), arrow({ element: arrowRef })],
  });

  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  }[placement.split('-')[0]];

  return (
    <>
      <div ref={reference} {...getReferenceProps()}>
        {children}
      </div>
      <FloatingPortal>
        {open && (
          <div
            ref={floating}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
              width: 'max-content',
              maxWidth: '300px',
            }}
            className={styles.customTooltip}
            {...getFloatingProps()}
          >
            <div
              ref={arrowRef}
              className={styles.customTooltipArrow}
              style={{
                left: arrowX != null ? `${arrowX}px` : '',
                top: arrowY != null ? `${arrowY}px` : '',
                right: '',
                bottom: '',
                [staticSide!]: '-4px',
              }}
            />
            <div className={styles.customTooltipContent}>{tooltipText}</div>
          </div>
        )}
      </FloatingPortal>
    </>
  );
};

const FZMenuTable = <TData extends object>({
  data = [],
  columns,
  enableFilters = false,
  enableMultiRowSelection = true,
  noRowsMessage = '',
  title,
  selectedRows,
  onRowSelectionChange,
  isLoading,
  accessTableApi,
  showPagination = false,
  meta,
  isFranchiseeEditAndFreeze,
  changeHeaderSize,
}: TableCardProps<StoreProduct>) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState({});

  const id = useId();
  const [headerIsSticky, setHeaderIsSticky] = useState(false);
  const { alias } = useFranchiseeAlias();
  const { data: dataUser } = useCurrentUser(true);

  const isUserViewOnlyAccess = dataUser?.data.role === UserRoles.VIEW;

  useEffect(() => {
    const stickyHeaderHandler = () => {
      if (window.scrollY > 210) {
        setHeaderIsSticky(true);
      } else {
        setHeaderIsSticky(false);
      }
    };

    window.addEventListener('scroll', stickyHeaderHandler);

    return () => {
      window.removeEventListener('scroll', stickyHeaderHandler);
    };
  }, []);

  // if we have an onRowSelectionChange prop, we assume selection state is being managed externally (via the selectedRows prop)
  const getRowSelectionHandler = () => {
    if (onRowSelectionChange) return onRowSelectionChange;
    return setRowSelection;
  };

  // if we receive selectedRows from props, update our internal state
  useEffect(() => {
    if (selectedRows) {
      setRowSelection(selectedRows);
    }
  }, [selectedRows]);

  const renderCheckboxCell = useCallback(
    (params: { row: Row<StoreProduct> }) => {
      const { priceLock, mandatoryProductLock, storeGroups } = params.row.original;
      const shouldShowPadlock = priceLock && hasActivePrice(storeGroups);
      const shouldShowEye = mandatoryProductLock;

      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            width: '120px',
            height: '100%',
            minWidth: '120px',
            maxWidth: '120px',
            gap: '6px',
            padding: '0 12px',
            boxSizing: 'border-box',
          }}
        >
          {shouldShowEye && (
            <IconWithTooltip tooltipText="This product has been set as mandatory and cannot be deactivated.">
              <VisibilityIcon
                style={{ color: '#266DCC' }}
                data-testid="mandatory-eye-icon"
                sx={{
                  width: '24px',
                  height: '24px',
                  cursor: 'help',
                  flexShrink: 0,
                }}
              />
            </IconWithTooltip>
          )}
          {shouldShowPadlock && (
            <IconWithTooltip tooltipText="The price for this product has been locked. If the product is inactive on the menu, you may activate it. However, no further price changes will be allowed while locked.">
              <LockOutlinedIcon
                style={{ color: '#266DCC' }}
                data-testid="price-lock-icon"
                sx={{
                  width: '24px',
                  height: '24px',
                  cursor: 'help',
                  flexShrink: 0,
                }}
              />
            </IconWithTooltip>
          )}
          {!isFranchiseeEditAndFreeze && !isUserViewOnlyAccess && (
            <div style={{ marginLeft: 'auto' }}>
              <Checkbox
                label=""
                data-testid="checkbox-icon"
                aria-label="select row"
                id={`${id}-row-${params.row.id}-checkbox`}
                checked={params.row.getIsSelected()}
                onChange={params.row.getToggleSelectedHandler()}
                disabled={!params.row.getCanSelect()}
              />
            </div>
          )}
        </div>
      );
    },
    [id, isFranchiseeEditAndFreeze, isUserViewOnlyAccess],
  );

  const renderHeaderCell = useCallback(
    (params: { table: TableType<unknown> }) => {
      if (!isFranchiseeEditAndFreeze && !isUserViewOnlyAccess) {
        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              width: '120px',
              height: '100%',
              minWidth: '120px',
              maxWidth: '120px',
              gap: '6px',
              padding: '0 12px',
              boxSizing: 'border-box',
            }}
          >
            <div style={{ marginLeft: 'auto' }}>
              <Checkbox
                label=""
                data-testid="select-all-menu-products"
                aria-label="select all rows"
                id={`${id}-header-checkbox`}
                checked={params.table.getIsAllRowsSelected()}
                onChange={params.table.getToggleAllRowsSelectedHandler()}
              />
            </div>
          </div>
        );
      }

      return null;
    },
    [id, isFranchiseeEditAndFreeze, isUserViewOnlyAccess],
  );

  const columnsWithRowSelection = useMemo(() => {
    const result = [];

    // Add checkbox column first
    result.push({
      id: 'select',
      header: renderHeaderCell,
      cell: renderCheckboxCell,
      columnClassname: isFranchiseeEditAndFreeze && isUserViewOnlyAccess ? 'headerWithoutCheckbox' : 'checkboxHeader',
    } as ColumnDef<StoreProduct, any>);

    // Add all other columns in their original order
    result.push(...columns);

    return result;
  }, [columns, isFranchiseeEditAndFreeze, isUserViewOnlyAccess, renderCheckboxCell, renderHeaderCell]);

  const getPrinceByOrderAndSort = (item: StoreProduct, sort: string, columnId: string) => {
    const chosenPriceRange = item.storeGroups
      .find((sg) => sg.storeGroupName === columnId)
      ?.chosenPriceRange?.split('-') as unknown as string[];

    if (!chosenPriceRange) return '';

    if (chosenPriceRange.length === 1) return Number(chosenPriceRange[0]);

    if (sort === 'asc') return Number(chosenPriceRange[0]);

    if (sort === 'desc') return Number(chosenPriceRange[1]);

    return Number(chosenPriceRange[0]);
  };

  const getChosenPriceRangeBySort = (rowA: Row<StoreProduct>, rowB: Row<StoreProduct>, columnId: string) => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const tableSortingState = table.getState().sorting;
    const tableSorting = tableSortingState.find((sort) => sort.id === columnId);
    const currentSort = tableSorting?.desc ? 'desc' : 'asc';
    const priceRangeA = getPrinceByOrderAndSort(rowA.original, currentSort, columnId);
    const priceRangeB = getPrinceByOrderAndSort(rowB.original, currentSort, columnId);

    if (Number.isNaN(priceRangeA) || Number.isNaN(priceRangeB)) return -1;

    const isEqual = priceRangeA === priceRangeB;
    if (isEqual) return 0;

    return priceRangeA > priceRangeB ? 1 : -1;
  };

  const sortProductsByProductNameAndProductDescription = (rowA: Row<StoreProduct>, rowB: Row<StoreProduct>) => {
    if (rowA.original.productName < rowB.original.productName) return -1;
    if (rowA.original.productName > rowB.original.productName) return 1;
    if (rowA.original.productName === rowB.original.productName) {
      if (rowA.original.productDescription < rowB.original.productDescription) return -1;
      if (rowA.original.productDescription > rowB.original.productDescription) return 1;
    }
    return 0;
  };

  const table = useReactTable({
    data,
    columns: columnsWithRowSelection,
    getCoreRowModel: getCoreRowModel(),
    enableFilters,
    enableMultiRowSelection,
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
      rowSelection,
    },
    onSortingChange: setSorting,
    onRowSelectionChange: getRowSelectionHandler(),
    getSortedRowModel: getSortedRowModel(),
    meta,
    debugTable: false,
    sortingFns: {
      sortProductByPrice: getChosenPriceRangeBySort,
      sortProduct: sortProductsByProductNameAndProductDescription,
    },
  });

  useEffect(() => {
    if (accessTableApi) accessTableApi(table);
  }, [table, accessTableApi]);

  const getAriaSort = (sortStatus: 'asc' | 'desc' | false) => {
    switch (sortStatus) {
      case 'asc':
        return 'ascending';
      case 'desc':
        return 'descending';
      default:
        return 'none';
    }
  };

  const filteredRowCount = table.getFilteredRowModel().rows.length;

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [hasVerticalScrollbar, setHasVerticalScrollbar] = useState(false);

  const checkForScrollbar = useCallback(() => {
    if (!tableContainerRef.current) return;
    if (tableContainerRef.current.clientHeight === tableContainerRef.current.scrollHeight) {
      setHasVerticalScrollbar(false);
    }
    if (tableContainerRef.current.clientHeight < tableContainerRef.current.scrollHeight) {
      setHasVerticalScrollbar(true);
    }
  }, []);

  useLayoutEffect(() => {
    checkForScrollbar();
  }, [isLoading, checkForScrollbar]);

  useEffect(() => {
    window.addEventListener('resize', checkForScrollbar);
    return () => window.removeEventListener('resize', checkForScrollbar);
  }, [checkForScrollbar]);

  function calculateMinWidth(columnsInternal: any[]): string {
    let minWidth: string;
    if (columnsInternal.length <= 5) {
      minWidth = '350px';
    } else {
      minWidth = `${columnsInternal.length * 2 + 280}px`;
    }
    return minWidth;
  }
  function calculateMaxTableSize(columnsInternal: any[]) {
    const maxTableSize = columnsInternal.length <= 7 ? '' : `${(columnsInternal.length - 5) * 5 + 100}vw`;
    changeHeaderSize(maxTableSize);
    return maxTableSize;
  }

  return (
    <div
      style={{ minWidth: calculateMaxTableSize(columns) }}
      ref={tableContainerRef}
      className={classNames(styles.tableContainer, {
        [styles.noTitle]: title === undefined,
        // [styles.loading]: isLoading,
        [styles.tableFilled]: showPagination,
        [styles.withVerticalScrollBar]: hasVerticalScrollbar,
      })}
    >
      <table className={styles.table} style={{ minWidth: '-webkit-fill-available' }}>
        <thead className={classNames({ [styles.whenSticky]: headerIsSticky, [styles.withAlias]: alias })}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  data-testid={`table-header-${header.id}`}
                  key={header.id}
                  className={classNames(
                    styles.columnHeader,
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    //@ts-ignore
                    styles[header.column.columnDef.columnClassname],
                  )}
                  aria-sort={getAriaSort(header.column.getIsSorted())}
                >
                  <div
                    data-testid={`table-sort-button-${header.id}`}
                    className={styles.columnHeaderTextAndIcon}
                    tabIndex={header.column.columnDef.enableSorting ? 0 : undefined}
                    aria-label={`sort column ${
                      typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : ''
                    }`}
                    onClick={
                      header.column.columnDef.enableSorting ? header.column.getToggleSortingHandler() : undefined
                    }
                    onTouchStart={() => null}
                    role="button"
                    onKeyDown={(event: KeyboardEvent) => {
                      switch (event.key) {
                        case ' ':
                        case 'Enter':
                          header.column.getToggleSortingHandler();
                          break;
                        default:
                          break;
                      }
                    }}
                  >
                    <span className={styles.columnHeaderText}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </span>
                    {header.column.columnDef.enableSorting && <SortIcon />}
                  </div>
                  {header.column.getCanFilter() ? (
                    <input
                      disabled={header.column.columnDef.meta?.disableFilter}
                      onChange={(event) => header.column.setFilterValue(event.target.value)}
                      value={(header.column.getFilterValue() as string) ?? ''}
                      className={classNames(inputStyles.input, styles.headerFilter)}
                      aria-label={`${header.column.columnDef.header} filter input`}
                      id={`${header.id}filterInput`}
                    />
                  ) : null}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {Children.toArray(
            table.getRowModel().rows.map((row, index) => (
              <tr
                key={row.id}
                data-testid="row-data"
                className={classNames(styles.row, {
                  [styles.even]: index % 2 === 0,
                  [styles.selectedRow]: row.getIsSelected(),
                })}
              >
                {row.getVisibleCells().map((cell) =>
                  cell?.column?.columnDef?.header !== 'PRODUCT' ||
                  typeof cell?.column?.columnDef?.header !== 'string' ? (
                    <td key={cell.id} className={styles.cell}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ) : (
                    <td
                      key={cell.id}
                      className={styles.cell}
                      style={{ minWidth: calculateMinWidth(columns), color: 'blue' }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ),
                )}
              </tr>
            )),
          )}
        </tbody>

        {!isLoading && filteredRowCount === 0 && <caption className={styles.noRowsMessage}>{noRowsMessage}</caption>}
      </table>
    </div>
  );
};

export default FZMenuTable;
